import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CurrentUser } from '../api.ts';
import { PLATFORM_SECTIONS, type PlatformSection } from '../pages/PlatformPage.tsx';
import { AccountMenu } from './AccountMenu.tsx';
import { AssociationIcon } from './AvatarIcons.tsx';
import {
  ConsoleIcon,
  DocumentsIcon,
  HoursIcon,
  ModerationIcon,
  NetworkIcon,
  NotificationsIcon,
  OnboardingIcon,
  ReportIcon,
  ShiftsIcon,
  SupervisionIcon,
  WidgetIcon,
} from './NavIcons.tsx';
import type { IconProps } from './PlatformIcons.tsx';

/**
 * L'hamburger che apre la barra su un telefono.
 *
 * Tre righe, non un'icona di libreria: stesso principio di
 * `PlatformIcons.tsx` — `currentColor`, `aria-hidden`, `focusable="false"` —
 * perché il nome accessibile del pulsante è la sua etichetta (`nav.label`,
 * riusata: non è una parola nuova), non l'icona.
 */
function MenuIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M3.5 6.5h17M3.5 12h17M3.5 17.5h17" />
    </svg>
  );
}

/**
 * Intestazione e navigazione della console.
 *
 * Le sezioni dipendono dal ruolo, e non è una comodità: chi non coordina non
 * deve vedere una voce che porta a una schermata che il server gli rifiuterà.
 * "Supervisione" compare a chi può fare il supervisore, "Segnalazioni" a chi
 * coordina — sono le due schermate della fase 6, e nessuna delle due è per il
 * volontario che risponde (sezione 7quater: la segnalazione non arriva mai al
 * volontario segnalato).
 *
 * Dalla fase 7 la barra serve **tre soggetti diversi**, e non sono tre ruoli
 * dello stesso ente:
 *
 *  - lo **staff dell'ente**, con in più "Rendicontazione" per chi coordina —
 *    una voce sola per storico, consumo e registro accessi, perché nove
 *    linguette su un telefono non sono navigazione (AG8);
 *  - il **fornitore**, che non ha nessuna delle voci dell'ente: le sue sono
 *    enti, avvisi, blacklist e monitoraggio. E quando è **entrato in un ente**
 *    per assistenza, la barra lo dice e offre l'uscita — perché un accesso
 *    impersonato che si dimentica aperto è la cosa peggiore che possa restare
 *    in piedi (sezione 9bis);
 *  - il **referente di rete**, che ha una voce sola perché vede una cosa sola.
 *
 * Sono pulsanti e non collegamenti perché non c'è nessuna pagina da caricare:
 * la navigazione dentro l'applicazione **non aggiunge voci alla cronologia del
 * browser** (sezione 7ter). Un `<a href>` le aggiungerebbe, e la cronologia è
 * esattamente ciò che per parte dell'utenza non deve raccontare niente.
 */

export type View =
  | 'console'
  | 'shifts'
  | 'notifications'
  | 'onboarding'
  | 'hours'
  | 'supervision'
  | 'moderation'
  | 'admin'
  | 'widget'
  | 'documents'
  | 'platform'
  | 'network';

/**
 * Il colore del fondo tenue dietro l'icona di una voce, per identità e non
 * per posizione.
 *
 * Una prima versione faceva ciclare i quattro colori con `nth-of-type`,
 * ricominciando da capo a ogni gruppo di navigazione: con più di quattro
 * voci — sette, per un coordinatore — la quinta voce riprendeva per caso il
 * colore della prima, la sesta quello della seconda, e così via, senza che
 * le due sezioni accostate avessero niente in comune. Non è la stessa cosa
 * di `.facts`, da cui il pattern viene: lì il ciclo sta dentro **una
 * scheda sola**, tutta visibile insieme, e si legge come una tavolozza
 * voluta; qui sono liste di lunghezza diversa per ruolo, e lo stesso ciclo
 * smetteva di distinguere le voci per confonderle in coppie casuali
 * (`docs/DECISIONI.md`, BQ1).
 *
 * Questo elenco fissa invece un colore per **ogni** identità di sezione,
 * nello stesso ordine in cui compaiono la prima volta in `sectionsFor` e in
 * `PLATFORM_SECTIONS`: "Rendicontazione" è sempre lo stesso colore ovunque
 * compaia, non più un colore che dipende da quali altre voci un dato ruolo
 * vede prima di lei.
 */
const NAV_ICON_COLOR_ORDER: (View | PlatformSection)[] = [
  'console',
  'shifts',
  'notifications',
  'onboarding',
  'supervision',
  'moderation',
  'hours',
  'admin',
  'widget',
  'documents',
  'platform',
  'network',
  'tenants',
  'new',
  'domains',
  'alerts',
  'blacklist',
  'clusters',
  'health',
  'access',
];

const NAV_ICON_COLORS = ['accent', 'kpi-2', 'kpi-3', 'kpi-4'] as const;

function navIconColorClass(id: View | PlatformSection): string {
  const index = NAV_ICON_COLOR_ORDER.indexOf(id);
  const color = NAV_ICON_COLORS[(index < 0 ? 0 : index) % NAV_ICON_COLORS.length];
  return `navlink__icon-wrap--${color}`;
}

type Props = {
  user: CurrentUser;
  view: View;
  onNavigate: (view: View) => void;
  onSignedOut: () => void;
  /** Uscire dall'ente in cui il fornitore è entrato per assistenza. */
  onEndImpersonation?: () => void;
  /**
   * Quale sezione della piattaforma è aperta, e come cambiarla.
   *
   * Rilevanti solo per il fornitore fuori da un ente: è l'unico soggetto le
   * cui otto voci vivono qui invece che in `sectionsFor` (`docs/DECISIONI.md`,
   * BP2). Opzionali perché ogni altro soggetto non li usa mai.
   */
  platformSection?: PlatformSection;
  onPlatformSectionChange?: (section: PlatformSection) => void;
};

/**
 * Le sezioni che un soggetto può vedere.
 *
 * Esportata perché la usa anche la radice dell'applicazione per decidere se
 * una sezione chiesta dall'indirizzo è raggiungibile: due elenchi che devono
 * dire la stessa cosa sono due elenchi che prima o poi divergono.
 */
export type Section = {
  id: View;
  labelKey: string;
  /**
   * A quale delle due famiglie appartiene questa voce.
   *
   * Le sezioni non sono tutte della stessa specie, e prima erano sette
   * pulsanti indistinguibili in fila con "notifiche" incastrata fra due
   * schermate di gestione. `mine` è ciò che riguarda il mio turno di stasera —
   * la console, i miei turni, come vengo avvisato — `tenant` è ciò che
   * riguarda l'ente. Un rigo verticale fra le due dice la differenza senza
   * aggiungere una parola, e chi usa uno screen reader la sente lo stesso:
   * sono due `<nav>` distinti, ognuno con la propria etichetta.
   */
  group: 'mine' | 'tenant';
  /**
   * L'icona della voce, come le otto del fornitore (`PlatformIcons.tsx`) —
   * prima solo lui ne aveva, e il resto della barra restava testo su testo
   * (`docs/DECISIONI.md`, BP3). `aria-hidden` di suo: il nome accessibile
   * resta `labelKey`, tradotto, non l'icona.
   */
  Icon: (props: IconProps) => React.ReactElement;
};

export function sectionsFor(user: CurrentUser): Section[] {
  if (user.kind === 'network_contact') {
    return [{ id: 'network', labelKey: 'nav.network', group: 'tenant', Icon: NetworkIcon }];
  }

  if (user.kind === 'platform_admin') {
    /**
     * Il fornitore **entrato in un ente**: vede le schermate del coordinatore,
     * in sola lettura (sezione 9bis). Non la console e non i turni — non entra
     * in turno e non risponde a nessuno — e non le decisioni, che sono
     * dell'ente.
     */
    if (user.impersonation) {
      return [
        { id: 'admin', labelKey: 'nav.admin', group: 'tenant', Icon: ReportIcon },
        { id: 'moderation', labelKey: 'nav.moderation', group: 'tenant', Icon: ModerationIcon },
        // Sola lettura anche qui (sezione 9bis): la rotta rifiuta la
        // scrittura del fornitore, la voce compare comunque perché le
        // "stesse schermate del coordinatore" comprendono anche questa.
        { id: 'documents', labelKey: 'nav.documents', group: 'tenant', Icon: DocumentsIcon },
      ];
    }
    return [{ id: 'platform', labelKey: 'nav.platform', group: 'tenant', Icon: ReportIcon }];
  }

  const supervises = user.role === 'supervisor' || user.role === 'coordinator';
  const coordinates = user.role === 'coordinator';

  /*
   * L'ordine, che prima mescolava i due registri.
   *
   * "Notifiche" — una preferenza personale, di questo dispositivo — stava fra
   * "Rendicontazione" e "Orari e turni", cioè fra due schermate di
   * amministrazione dell'ente. Adesso le tre voci di chi risponde stanno
   * insieme e in testa, e quelle di chi amministra dopo: chi non coordina
   * vede solo le prime tre, ed è la maggioranza delle persone che aprono
   * questa console.
   */
  return [
    { id: 'console', labelKey: 'nav.console', group: 'mine', Icon: ConsoleIcon },
    { id: 'shifts', labelKey: 'nav.shifts', group: 'mine', Icon: ShiftsIcon },
    { id: 'notifications', labelKey: 'nav.notifications', group: 'mine', Icon: NotificationsIcon },
    /*
      L'onboarding guidato: prima voce di "Gestione dell'ente", e solo per
      chi coordina. Non sostituisce le altre — restano raggiungibili una per
      una, com'erano — ma è il punto in cui un coordinatore appena entrato
      trova l'ordine e quanto manca (mock 5, `o-step1`…`o-step5`).
    */
    ...(coordinates
      ? [
          {
            id: 'onboarding' as const,
            labelKey: 'nav.onboarding',
            group: 'tenant' as const,
            Icon: OnboardingIcon,
          },
        ]
      : []),
    ...(supervises
      ? [
          {
            id: 'supervision' as const,
            labelKey: 'nav.supervision',
            group: 'tenant' as const,
            Icon: SupervisionIcon,
          },
        ]
      : []),
    ...(coordinates
      ? [
          {
            id: 'moderation' as const,
            labelKey: 'nav.moderation',
            group: 'tenant' as const,
            Icon: ModerationIcon,
          },
        ]
      : []),
    ...(coordinates
      ? [{ id: 'hours' as const, labelKey: 'nav.hours', group: 'tenant' as const, Icon: HoursIcon }]
      : []),
    ...(coordinates
      ? [
          {
            id: 'admin' as const,
            labelKey: 'nav.admin',
            group: 'tenant' as const,
            Icon: ReportIcon,
          },
        ]
      : []),
    /*
      L'aspetto del widget: ultima voce, e solo per chi coordina.

      È una configurazione che si tocca il primo giorno e poi quasi mai, quindi
      non contende il posto alle schermate che si aprono ogni sera. Ma deve
      esserci: fino a ieri il colore e i testi del proprio widget si cambiavano
      solo con una modifica al database, cioè scrivendo a noi — ed è la prima
      cosa che un ente chiede dopo aver incollato lo snippet.
    */
    ...(coordinates
      ? [
          {
            id: 'widget' as const,
            labelKey: 'nav.widget',
            group: 'tenant' as const,
            Icon: WidgetIcon,
          },
        ]
      : []),
    /*
      I documenti dell'ente (AN): contratto, nomina art. 28, e i tre da
      conservare. Ultima voce come "Aspetto del widget", per lo stesso
      motivo: si tocca poche volte, non ogni sera.
    */
    ...(coordinates
      ? [
          {
            id: 'documents' as const,
            labelKey: 'nav.documents',
            group: 'tenant' as const,
            Icon: DocumentsIcon,
          },
        ]
      : []),
  ];
}

export function TopBar({
  user,
  view,
  onNavigate,
  onSignedOut,
  onEndImpersonation,
  platformSection,
  onPlatformSectionChange,
}: Props) {
  const { t } = useTranslation();
  const sections = sectionsFor(user);
  const mine = sections.filter((section) => section.group === 'mine');
  const tenant = sections.filter((section) => section.group === 'tenant');

  /**
   * Sotto i 64rem la barra è dietro un hamburger, non più una fascia che
   * scorre in orizzontale (`docs/DECISIONI.md`, BP3): niente più `<nav>` a
   * mezza parola tagliata sul bordo dello schermo. `.app-sidebar__drawer`
   * (nav + profilo) è lo stesso markup di sempre, mostrato come pannello a
   * comparsa invece che come colonna fissa — cambia solo il CSS, non il
   * documento.
   */
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const drawerId = useId();
  const drawerRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  /**
   * `Esc` chiude e il fuoco torna sull'hamburger; `Tab` non esce mai dal
   * pannello mentre è aperto — un vero trap, non solo una chiusura al primo
   * fuoco perso come nel menu del profilo (`AccountMenu.tsx`), perché qui
   * dentro c'è un'intera navigazione e lasciare che il fuoco scappi nel
   * contenuto sottostante — invisibile, coperto dal pannello — sarebbe
   * disorientante per chi non vede lo schermo.
   */
  useEffect(() => {
    if (!mobileNavOpen) return;

    const focusableSelector =
      'a[href], button:not([disabled]), select, input, [tabindex]:not([tabindex="-1"])';
    const focusables = (): HTMLElement[] =>
      drawerRef.current
        ? Array.from(drawerRef.current.querySelectorAll<HTMLElement>(focusableSelector))
        : [];

    focusables()[0]?.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setMobileNavOpen(false);
        toggleRef.current?.focus();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusables();
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mobileNavOpen]);

  /*
   * Se lo schermo cresce oltre i 64rem con il pannello aperto — una tastiera
   * a schermo che si chiude, un tablet ruotato — la barra torna colonna fissa
   * da sola: un pannello "aperto" che il CSS ha già reso invisibile non deve
   * restare nello stato sbagliato per il prossimo giro sotto i 64rem.
   */
  useEffect(() => {
    const query = window.matchMedia('(min-width: 64rem)');
    const onChange = (): void => {
      if (query.matches) setMobileNavOpen(false);
    };
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const closeMobileNav = (): void => setMobileNavOpen(false);

  /**
   * Il fornitore fuori da un ente vede le otto sezioni della piattaforma
   * direttamente qui, non una voce sola che porta a un pannello con una
   * seconda navigazione dentro (`docs/DECISIONI.md`, BP2): per lui questa
   * *è* la console, come "I miei turni" e "Rendicontazione" lo sono per lo
   * staff di un ente.
   */
  const isPlatformOverview = user.kind === 'platform_admin' && !user.impersonation;

  const navButton = (section: Section) => (
    <button
      key={section.id}
      type="button"
      className={view === section.id ? 'navlink is-on' : 'navlink'}
      // Lo stato corrente è annunciato, non solo colorato (sezione 7bis).
      aria-current={view === section.id ? 'page' : undefined}
      onClick={() => {
        onNavigate(section.id);
        // Su un telefono il pannello copre la pagina: scelta una voce, si
        // chiude da sé — nessuno vuole richiudere l'hamburger a mano ogni
        // volta.
        closeMobileNav();
      }}
    >
      <span className={`navlink__icon-wrap ${navIconColorClass(section.id)}`}>
        <section.Icon className="navlink__icon" />
      </span>
      {t(section.labelKey)}
    </button>
  );

  return (
    <>
      {/*
        L'accesso impersonato si dichiara, e si può chiudere da qui.

        Sta **prima di tutto il resto**, ed è l'unico modo in cui arriva a chi
        non guarda lo schermo. Era in fondo alla barra e dentro un
        `role="status"`: due difetti che si annullavano a vicenda invece di
        sommarsi.

        La regione live non annunciava niente, e non poteva: si entra in un ente
        con un `location.reload()`, quindi al primo disegno di questa pagina
        l'avviso c'è già. Una regione live dice cosa **cambia** dopo che è
        stata montata, e qui non cambia mai niente — più un `<button>` dentro
        una regione live, che alcuni screen reader rileggono per intero a ogni
        cambio di stato del pulsante.

        Quello che serve non è un annuncio: è che la dichiarazione stia
        **nel flusso di lettura**, prima della barra laterale e di tutto il
        resto (sezione 7bis, la stessa regola del 112 nel widget). Chi legge la
        pagina dall'inizio sente di essere in assistenza prima di sentire in
        quale ente.

        Il pulsante è fuori dal paragrafo: è un'azione, non una frase. Sta
        **fuori** da `.sidebar`, a piena larghezza sopra di lei: e' un avviso
        sull'intera pagina, non sulla sola colonna di navigazione.
      */}
      {user.impersonation ? (
        <div className="impersonation-bar">
          <p>{t('platform.impersonating', { name: user.tenant?.name ?? '' })}</p>
          {onEndImpersonation ? (
            <button type="button" className="button" onClick={onEndImpersonation}>
              {t('platform.impersonation_end')}
            </button>
          ) : null}
        </div>
      ) : null}

      {/*
        La barra laterale.

        Sotto i 64rem una colonna fissa di 17rem è metà schermo tolta al
        contenuto (sezione 11), quindi non resta a schermo: diventa un
        pannello a comparsa dietro l'hamburger nel riquadro qui sotto, con
        lo stesso documento (nome dell'ente, navigazione, profilo) mostrato
        come overlay invece che come colonna — non più come fascia
        orizzontale che va a capo, che tagliava a metà parola le voci più
        lunghe e costringeva lo scorrimento laterale
        (`docs/DECISIONI.md`, BP3).
      */}
      {/*
        `<header>`, non `<aside>`: il ruolo d'accessibilità è `banner`, lo
        stesso di prima — un `<aside>` avrebbe dato `complementary`, e chi
        naviga per punti di riferimento avrebbe perso il modo di saltare
        dritto all'intestazione della pagina. La forma visiva (colonna a
        sinistra invece di fascia in alto) è solo CSS: l'elemento giusto lo
        decide cosa **è**, non dove sta a schermo.
      */}
      <header className="app-sidebar">
        <div className="app-sidebar__brand">
          {/*
            L'hamburger, solo sotto i 64rem (nascosto in CSS sopra quella
            soglia — un pulsante `display: none` non è raggiungibile da
            tastiera, quindi sopra i 64rem non ruba una tappa in più a chi
            tabula).

            `aria-controls` punta al pannello vero, `aria-expanded` dice se è
            aperto: lo stesso modello a divulgazione del menu del profilo
            (`AccountMenu.tsx`), qui su un pannello con dentro un'intera
            navigazione invece di due tendine.
          */}
          <button
            type="button"
            ref={toggleRef}
            className="app-sidebar__toggle"
            aria-expanded={mobileNavOpen}
            aria-controls={drawerId}
            aria-label={t('nav.label')}
            onClick={() => setMobileNavOpen((open) => !open)}
          >
            <MenuIcon className="app-sidebar__toggle-icon" />
          </button>

          {/*
            Il quadratino dell'ente: mani unite, non più le sue iniziali
            (`docs/DECISIONI.md`, BQ1).

            È `aria-hidden` perché non porta nessuna informazione: il nome per
            esteso è nel titolo qui accanto. Resta comunque utile a chi
            guarda — il fondo viola distingue questa colonna dal resto della
            pagina a colpo d'occhio — ma non distingue più un ente dall'altro
            da solo: chi assiste più enti — o ne coordina due — lo fa ormai
            leggendo il nome accanto, non più due iniziali diverse.
          */}
          <span className="app-sidebar__mark" aria-hidden="true">
            <AssociationIcon />
          </span>

          {/*
            Il nome dell'ente, non "Console": in cima c'era la stessa parola del
            primo pulsante di navigazione, e chi assiste più enti — o li coordina
            entrambi — deve vedere in quale sta lavorando senza cercarlo.

            In assistenza il titolo lo **dice**, e non è una ripetizione del
            riquadro qui sopra: chi naviga per intestazioni — il modo più comune
            di attraversare una pagina con uno screen reader — salta il riquadro
            e arriva qui. Trovandoci solo il nome dell'ente, un fornitore cieco
            non aveva ancora nessun modo di sapere di essere in casa di qualcun
            altro (sezione 9bis).
          */}
          <h1
            title={
              user.impersonation && user.tenant
                ? `${user.tenant.name} — ${t('platform.impersonation_badge')}`
                : (user.tenant?.name ?? t('console.title'))
            }
          >
            {user.impersonation && user.tenant
              ? `${user.tenant.name} — ${t('platform.impersonation_badge')}`
              : (user.tenant?.name ?? t('console.title'))}
          </h1>
        </div>

        {/*
          Lo sfondo dietro il pannello, solo mentre è aperto: chiude al
          tocco, come qualunque pannello a comparsa. Non è la sola via di
          chiusura — `Esc` e la voce scelta lo sono già — quindi resta un
          `<div>` semplice, senza fuoco proprio: non toglie né aggiunge
          tappe a chi tabula.
        */}
        {mobileNavOpen ? (
          <div className="app-sidebar__backdrop" aria-hidden="true" onClick={closeMobileNav} />
        ) : null}

        {/*
          Il pannello: navigazione, poi profilo — in quest'ordine nel
          documento **e** a schermo, sia qui sotto i 64rem (un pannello a
          comparsa, verticale) sia sopra (la colonna fissa, sotto).

          Prima il profilo veniva **prima** della navigazione nel documento
          apposta, per tenerlo in cima a schermo sotto i 64rem senza
          disallineare l'ordine di tabulazione (`docs/DECISIONI.md`, BP1) —
          ma il proprietario voleva il profilo in **fondo** alla colonna,
          come nel mock, e le due cose in quella forma erano davvero
          incompatibili: o il profilo stava per primo nel documento (fuori
          sincrono con "in fondo"), o per ultimo (fuori sincrono con "in
          cima" sotto i 64rem). Sparito il bisogno di far stare tutto su
          un'unica fascia orizzontale — ora sotto i 64rem è un pannello
          verticale a comparsa, non più una fascia che va a capo — il
          conflitto sparisce con lui: la navigazione **prima**, il profilo
          **dopo**, sempre, in entrambe le forme (`docs/DECISIONI.md`, BP3).
        */}
        <div
          id={drawerId}
          ref={drawerRef}
          className="app-sidebar__drawer"
          data-open={mobileNavOpen}
        >
          {/*
            Due gruppi di voci, non sette pulsanti in fila.

            Sono due `<nav>` con due etichette diverse perché la differenza fra
            "il mio turno" e "l'ente" deve arrivare anche a chi la pagina la
            ascolta: chi naviga per punti di riferimento sente due elenchi
            distinti e sa in quale dei due cercare. Per chi guarda, la stessa
            cosa la dice l'etichetta piccola e maiuscoletta sopra ciascun
            gruppo — lo stesso testo dell'`aria-label`, non un'invenzione nuova.

            Sono pulsanti e non collegamenti perché non c'è nessuna pagina da
            caricare: la navigazione dentro l'applicazione **non aggiunge voci
            alla cronologia del browser** (sezione 7ter). Un `<a href>` le
            aggiungerebbe, e la cronologia è esattamente ciò che per parte
            dell'utenza non deve raccontare niente.

            Con una sezione sola non compare affatto — è tornato il controllo
            che il primo disegno di questo restyling aveva perso per strada:
            il referente di rete ha una voce e basta, e una navigazione con
            dentro un solo pulsante sempre acceso non è navigazione
            (`docs/DECISIONI.md`, BP1). Il fornitore fuori da un ente non
            passa più da qui: le sue otto voci sono il ramo qui sotto.
          */}
          {isPlatformOverview ? (
            <div className="app-sidebar__nav">
              <nav aria-label={t('platform.sections_label')} className="app-sidebar__group">
                {PLATFORM_SECTIONS.map(({ id, Icon }) => (
                  <button
                    key={id}
                    type="button"
                    className={platformSection === id ? 'navlink is-on' : 'navlink'}
                    aria-current={platformSection === id ? 'page' : undefined}
                    onClick={() => {
                      onPlatformSectionChange?.(id);
                      closeMobileNav();
                    }}
                  >
                    <span className={`navlink__icon-wrap ${navIconColorClass(id)}`}>
                      <Icon className="navlink__icon" />
                    </span>
                    {t(`platform.section_${id}`)}
                  </button>
                ))}
              </nav>
            </div>
          ) : sections.length > 1 ? (
            <div className="app-sidebar__nav">
              {mine.length > 0 ? (
                <nav aria-label={t('nav.label_mine')} className="app-sidebar__group">
                  <p className="app-sidebar__group-label" aria-hidden="true">
                    {t('nav.label_mine')}
                  </p>
                  {mine.map(navButton)}
                </nav>
              ) : null}

              {tenant.length > 0 ? (
                <nav
                  aria-label={mine.length > 0 ? t('nav.label_tenant') : t('nav.label')}
                  className="app-sidebar__group"
                >
                  <p className="app-sidebar__group-label" aria-hidden="true">
                    {mine.length > 0 ? t('nav.label_tenant') : t('nav.label')}
                  </p>
                  {tenant.map(navButton)}
                </nav>
              ) : null}
            </div>
          ) : null}

          <div className="app-sidebar__footer">
            <AccountMenu user={user} onSignedOut={onSignedOut} />
          </div>
        </div>
      </header>
    </>
  );
}
